import * as React from 'react';
import classNames from 'classnames';
import { Link } from 'react-router';
import { Theme } from '@mui/material/styles';
import { withStyles } from 'tss-react/mui';
import EDAvatar from '../../BaseComponents/Avatar/EDAvatar';
import ProcessWarningOverlay from '../../Features/Processes/Components/ProcessWarningOverlay/ProcessWarningOverlay';
import EDOverlay from '../../BaseComponents/Overlay/EDOverlay';
import EDIconButton from '../../BaseComponents/Buttons/IconButton/EDIconButton';
import { IDatabaseListProps } from '../DatabaseList/DatabaseList';
import { IDatabase } from '../../Features/Login/Contexts/LoginStateContext/LoginStateContext';

interface IProps extends IDatabaseListProps {
  onClick: (db: IDatabase) => void;
  getLinkButtonTarget?: (db: IDatabase) => string;
  classes?: any;
}

const styles = (theme: Theme) => ({
  avatarLargeContainer: {
    marginLeft: theme.spacing(1),
    marginRight: theme.spacing(1),
  },
  avatarSmall: {
    width: theme.spacing(3),
    height: theme.spacing(3),
    fontSize: theme.typography.pxToRem(14),
  },
  avatarLarge: {
    width: theme.spacing(4),
    height: theme.spacing(4),
    fontSize: theme.typography.pxToRem(20),
  },
  unselectedAvatar: {
    background: theme.customProperties.AvatarList.inactiveBackground,
    color: theme.customProperties.AvatarList.inactiveTextColor,
    margin: theme.customProperties.AvatarList.inactiveBorder,
    '&:hover': {
      background: theme.customProperties.AvatarList.inactiveHoverBackground,
      color: theme.customProperties.AvatarList.inactiveHoverTextColor,
      margin: theme.customProperties.AvatarList.inactiveHoverBorder,
    },
  },
  selectedAvatar: {
    background: theme.customProperties.AvatarList.activeBackground,
    color: theme.customProperties.AvatarList.activeTextColor,
    border: theme.customProperties.AvatarList.activeBorder,
    '&:hover': {
      background: theme.customProperties.AvatarList.activeHoverBackground,
      color: theme.customProperties.AvatarList.activeHoverTextColor,
      border: theme.customProperties.AvatarList.activeHoverBorder,
    },
  },
  avatarLinkButton: {
    textDecoration: 'none',
  },
  warningOverlayPosition: {
    bottom: '-4px',
    right: '-4px',
  },
});

const capitalize = (str: string): string => {
  return str
    .split(' ')
    .map(
      w =>
        (w.length > 0 ? w[0].toLocaleUpperCase() : '') +
        (w.length > 1 ? w.slice(1).toLowerCase() : '')
    )
    .join(' ');
};

export const getAvatarName = (
  databaseName: string,
  usedAvatarNames: string[]
): string => {
  let name = databaseName.substring(0, 2);
  let len = databaseName.length;
  let i = 0;
  let j = 1;
  while (usedAvatarNames.includes(name.toLowerCase())) {
    j++;
    if (j >= len) {
      i++;
      if (i >= len) {
        return '??';
      }
      j = i + 1;
    }
    name = databaseName.substring(i, i + 1) + databaseName.substring(j, j + 1);
  }
  usedAvatarNames.push(name.toLowerCase());
  return capitalize(name);
};

const isSelected = (selectedDbs: IDatabase[], db: IDatabase) => {
  return selectedDbs.filter(x => x.id === db.id).length > 0;
};

/**
 * Komponent för att visa vilka enheter användaren har tillgång till. Passar till liten skärm storlek.
 */
export class DatabaseAvatarList extends React.Component<IProps> {
  renderButton(
    db: IDatabase,
    onClick?: (db: IDatabase) => void,
    warning?: string
  ) {
    const usedAvatarNames: string[] = [];
    return (
      <EDIconButton
        icon={
          <EDAvatar
            className={classNames(
              this.props.size === 'small'
                ? this.props.classes.avatarSmall
                : this.props.classes.avatarLarge,
              isSelected(this.props.selectedDatabases, db)
                ? this.props.classes.selectedAvatar
                : this.props.classes.unselectedAvatar
            )}
          >
            {getAvatarName(db.name, usedAvatarNames)}
          </EDAvatar>
        }
        onClick={onClick ? () => onClick(db) : () => {}}
        tooltip={`${capitalize(db.name)}${warning ? ' - ' + warning : ''}}`}
        margin="none"
      />
    );
  }

  render() {
    const onClick = this.props.onClick;
    const getLinkButtonTarget = this.props.getLinkButtonTarget;

    return (
      <React.Fragment>
        {this.props.databases.map((db: IDatabase) => {
          const warning =
            this.props.warnings &&
            this.props.warnings.find(
              w =>
                w.database.toLocaleLowerCase() === db.name.toLocaleLowerCase()
            );
          return (
            <EDOverlay
              key={db.id}
              className={classNames({
                [this.props.classes.avatarLargeContainer]:
                  this.props.size !== 'small',
              })}
              overlay={warning && <ProcessWarningOverlay />}
              classes={{ overlay: this.props.classes.warningOverlayPosition }}
            >
              {onClick &&
                !getLinkButtonTarget &&
                this.renderButton(db, onClick, warning && warning.message)}
              {getLinkButtonTarget && (
                <Link
                  to={getLinkButtonTarget(db)}
                  className={this.props.classes.avatarLinkButton}
                >
                  {this.renderButton(db, onClick, warning && warning.message)}
                </Link>
              )}
            </EDOverlay>
          );
        })}
      </React.Fragment>
    );
  }
}

export default withStyles(DatabaseAvatarList, styles);
